/* Shared wiring for the ui/fields controls.
 *
 * Every control renders <Field> around one element and needs the same four
 * things joined up: an id (the caller's, or a generated one), the label's
 * htmlFor, aria-describedby pointing at whichever line Field is showing
 * (the error replaces the hint, so only one is ever in the DOM), and
 * aria-invalid / aria-required.
 *
 * Required is announced with aria-required, not the native `required`
 * attribute, on purpose: native required makes the browser block submit
 * with its own bubble, and IMT forms validate themselves and show the
 * message in the Field's error line. Swapping a hand-built input for a
 * block must never add that bubble.
 */
import { useId, useImperativeHandle, useRef } from 'react';

const joinIds = (...ids) => {
  const list = ids.filter(Boolean);
  return list.length ? list.join(' ') : undefined;
};

export const cx = (...names) => names.filter(Boolean).join(' ');

/* `aria` carries the caller's own aria-describedby / aria-invalid /
 * aria-required, so a pass-through value is merged or kept, never dropped. */
export default function useFieldControl({
  id,
  hint,
  error,
  required = false,
  aria = {},
}) {
  const autoId = useId();
  const controlId = id || `hms-field-${autoId.replace(/:/g, '')}`;
  const hintId = `${controlId}-hint`;
  const errorId = `${controlId}-error`;

  let shownLineId = null;
  if (error) shownLineId = errorId;
  else if (hint) shownLineId = hintId;

  return {
    fieldProps: { htmlFor: controlId, hintId, errorId },
    controlProps: {
      id: controlId,
      'aria-invalid': error ? true : aria['aria-invalid'],
      'aria-required': required ? true : aria['aria-required'],
      'aria-describedby': joinIds(aria['aria-describedby'], shownLineId),
    },
  };
}

/* Pulls the three aria props the hook merges out of a control's rest props,
 * so they are not also spread raw onto the element. */
export const splitAria = ({
  'aria-describedby': describedBy,
  'aria-invalid': invalid,
  'aria-required': ariaRequired,
  ...rest
}) => [
  { 'aria-describedby': describedBy, 'aria-invalid': invalid, 'aria-required': ariaRequired },
  rest,
];

/* AmountInput and DatePicker do not forward refs, and neither file is ours
 * to change here. So the block points the caller's ref at the <input> they
 * render, found inside the Field's own div once it has mounted. Returns the
 * ref to put on <Field>. */
export function useInnerInputRef(ref) {
  const rootRef = useRef(null);
  useImperativeHandle(
    ref,
    () => (rootRef.current ? rootRef.current.querySelector('input') : null),
    []
  );
  return rootRef;
}
